import React, { useState, useEffect } from 'react';
import {
  Modal,
  ConfirmDialog,
  Pagination,
  AccessDeniedMessage,
  AccessDeniedModalContent,
} from '../../molecules';
import { LikeSearchBar } from '../../molecules/LikeSearchBar';
import { LikesPageHeader } from '../../molecules/LikesPageHeader';
import { LikesList } from '../../organisms';
import { likesService, type Like, type LikeTargetType } from '../../../services/likesService';

type FilterType = 'all' | LikeTargetType;

export const LikesPage = () => {
  const [likes, setLikes] = useState<Like[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [requiredRoles, setRequiredRoles] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [targetFilter, setTargetFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [likeToDelete, setLikeToDelete] = useState<Like | null>(null);
  const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');
  const [accessDeniedRoles, setAccessDeniedRoles] = useState<string[]>([]);

  const loadLikes = async (
    page: number = 1,
    search?: string,
    filter: FilterType = 'all',
  ) => {
    try {
      setLoading(true);
      setError(null);
      const type = filter === 'all' ? undefined : filter;
      const response = await likesService.searchLikes(search, page, 20, type);
      setLikes(response.data);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
    } catch (err: any) {
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        setIsAccessDenied(true);
        setError(null);
        const roles = err?.response?.data?.requiredRoles || ['superuser', 'moderator'];
        setRequiredRoles(Array.isArray(roles) ? roles : []);
      } else {
        setError(
          err?.response?.data?.message ||
          err?.message ||
          'Beğeniler yüklenirken bir hata oluştu',
        );
        setIsAccessDenied(false);
      }
      console.error('Failed to load likes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLikes(1, searchQuery, targetFilter);
  }, [searchQuery, targetFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadLikes(1, searchQuery, targetFilter);
  };

  const handleTargetFilterChange = (filter: FilterType) => {
    setTargetFilter(filter);
    setCurrentPage(1);
  };

  const handleDeleteClick = (like: Like) => {
    setLikeToDelete(like);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!likeToDelete) return;

    try {
      setDeletingId(likeToDelete.id);
      setShowDeleteConfirm(false);
      await likesService.deleteLike(likeToDelete.id);

      const shouldGoToPreviousPage = likes.length === 1 && currentPage > 1;
      const pageToLoad = shouldGoToPreviousPage ? currentPage - 1 : currentPage;
      await loadLikes(pageToLoad, searchQuery, targetFilter);
    } catch (err: any) {
      console.error('Failed to delete like:', err);
      if (err?.response?.status === 403 || err?.message === 'Erişim Reddedildi') {
        const roles = err?.response?.data?.requiredRoles || ['superuser'];
        setAccessDeniedMessage('Bu beğeniyi silmek için gerekli yetkilere sahip değilsiniz.');
        setAccessDeniedRoles(Array.isArray(roles) ? roles : []);
        setShowAccessDeniedModal(true);
      } else {
        alert(err instanceof Error ? err.message : 'Beğeni silinirken bir hata oluştu');
      }
    } finally {
      setDeletingId(null);
      setLikeToDelete(null);
    }
  };

  if (isAccessDenied) {
    return (
      <AccessDeniedMessage
        message="Bu beğenilere erişmek için gerekli yetkilere sahip değilsiniz."
        requiredRoles={requiredRoles}
        onBack={() => window.history.back()}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-gray-50 to-white min-h-screen">
      <LikesPageHeader />

      <LikeSearchBar
        searchQuery={searchQuery}
        targetFilter={targetFilter}
        onSearchQueryChange={setSearchQuery}
        onTargetFilterChange={handleTargetFilterChange}
        onSearch={handleSearch}
      />

      {loading ? (
        <div className="text-center text-gray-500 py-8">Beğeniler yükleniyor...</div>
      ) : error ? (
        <div className="text-center text-red-500 py-8">{error}</div>
      ) : likes.length === 0 ? (
        <div className="text-center text-gray-500 py-8">Beğeni bulunamadı</div>
      ) : (
        <>
          <LikesList
            likes={likes}
            deletingId={deletingId}
            onDeleteClick={handleDeleteClick}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => loadLikes(page, searchQuery, targetFilter)}
          />
        </>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setLikeToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Beğeniyi Sil"
        message={
          <div>
            <p className="text-gray-700">
              Bu beğeniyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
          </div>
        }
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        isLoading={deletingId !== null}
      />

      <Modal
        isOpen={showAccessDeniedModal}
        onClose={() => setShowAccessDeniedModal(false)}
        title="Erişim Reddedildi"
        size="medium"
      >
        <AccessDeniedModalContent
          message={accessDeniedMessage}
          requiredRoles={accessDeniedRoles}
          onClose={() => setShowAccessDeniedModal(false)}
        />
      </Modal>
    </div>
  );
};
